'use client';

/**
 * API namespace `admin.market.coupons` (sub-namespace extracted).
 * Verbatim extraction from frontend/src/lib/api-client.ts (originally within admin.market).
 *
 * Part of SPEC-244 IA17 split (refactor/spec244-ia17-api-client).
 * Transformations: T-DEDENT-2 (relative to admin.market), T-EXPORT, T-IMPORTS.
 */

import { apiFetch, apiFetchFormData } from '../core/fetch-client';
import type {
  AdminDisputeListFilters, AdminDisputeListResponse,
  AdminDisputeDetailResponse, AdminResolveDisputeBody,
  AdminResolveAppealBody, AdminModerateReviewBody,
  DisputeResponse, DisputeListResponse, DisputeMessageResponse,
  EvidenceUploadResponse, AppealResponse, ReviewResponse,
  BundleStats, CouponStats, GiftCardStats,
} from '../core/types';
import type {
  AdminProductListItem, AdminProductListResponse,
  ProductListResponse, PublicProductResponse, ProductListFilters,
  Dispute, DisputeStatus, DisputeChannel,
  Evidence, EvidenceType, DisputeMessage,
  Review, ReviewDirection, ReviewRating,
  ReviewListItem, ReviewListResponse,
  ProductBundle, ProductBundleResponse, ProductBundleListResponse,
  ProductBundleStatus, ProductBundleCreateInput, ProductBundleUpdateInput,
  Coupon, CouponResponse, CouponListResponse, CouponStatus,
  CouponPurchaseResponse, CouponPurchaseListResponse,
  CouponCreateInput, CouponUpdateInput, CouponPurchaseInput,
  GiftCard, GiftCardResponse, GiftCardListResponse, GiftCardStatus,
  GiftCardPurchaseResponse, GiftCardPurchaseListResponse,
  GiftCardCreateInput, GiftCardUpdateInput, GiftCardPurchaseInput,
  RedemptionMethod, PurchaseStatus,
  ProductCreateRequest, ProductUpdateRequest,
  SellerProductResponse, ProductStatus,
  Paginated,
} from '../core/shared-types';

export const coupons = {
  /**
   * GET /api/admin/market/coupons — admin list of ALL coupons across
   * ALL sellers (paginated, 1-indexed). Returns `CouponListResponse`
   * (`coupons: CouponResponse[]` + `total` + `page` + `limit`). Each
   * coupon in the list is the EXPANDED snapshot (includes the
   * seller's storeName + storeSlug snapshot — same wire shape as the
   * public catalog response per SPEC-242 §3.5).
   *
   * Query params (all optional):
   *   - `page`             (default 50, max 100) — page number.
   *   - `limit`            (default 50, max 100) — page size.
   *   - `status`           — one of `CouponStatus` (5 values:
   *                          pending_approval / approved / rejected /
   *                          paused / expired). Invalid values
   *                          are silently ignored by the backend.
   *   - `sellerId`         — filter by Seller.sellerId (UUID). Invalid
   *                          values are passed through to the service
   *                          (returns 0 results for unknown sellerIds).
   *   - `includeInactive`  (default true) — when 'false', excludes
   *                          soft-deleted coupons.
   *
   * Authz: `requireAuth` + `requireAdminRead` (router-level). NO
   * 2FA required for the GET (read-only — INV-SEC-06 §9.6 only
   * applies to financial-grade state transitions).
   *
   * Spec ref: SPEC-242 §3.5 (GET /admin/market/coupons), §3.6.5
   *           (admin UI — table of coupons awaiting moderation).
   */
  async list(
    filters?: {
      page?: number;
      limit?: number;
      status?: CouponStatus;
      sellerId?: string;
      includeInactive?: boolean;
    },
    signal?: AbortSignal,
  ): Promise<CouponListResponse> {
    const qs = new URLSearchParams();
    if (filters?.page !== undefined) qs.set('page', String(filters.page));
    if (filters?.limit !== undefined) qs.set('limit', String(filters.limit));
    if (filters?.status) qs.set('status', filters.status);
    if (filters?.sellerId) qs.set('sellerId', filters.sellerId);
    if (filters?.includeInactive !== undefined) {
      qs.set('includeInactive', String(filters.includeInactive));
    }
    const query = qs.toString();
    return apiFetch<CouponListResponse>(
      query ? `/admin/market/coupons?${query}` : '/admin/market/coupons',
      { signal },
    );
  },
  /**
   * PUT /api/admin/market/coupons/:couponId/approve — approve a
   * pending coupon (INV-MKT-20). The backend atomically transitions
   * `status: 'pending_approval' → 'approved'` via CAS
   * (`findOneAndUpdate({ couponId, status: 'pending_approval' })`).
   * If the CAS filter fails (another admin rejected between this
   * admin's read + write), the service throws 409
   * 'concurrent modification'.
   *
   * AuditLog 'COUPON_APPROVED' is written (INV-SEC-08 hash chain).
   * NO UnifiedTransaction — coupons do NOT create financial
   * transactions on approve/reject (per task constraint — the
   * financial flow is buyer-side via `purchaseCoupon`).
   *
   * 2FA required (INV-SEC-06 §9.6 — per-route `requireRecent2FA`
   * in `routes/admin.route.ts`). On 403 `2FA_REQUIRED`, the
   * caller's catch block re-opens the `TwoFactorFinancialDialog`
   * (rendered by `/admin/market/cupones/page.tsx`).
   *
   * Path param: `couponId` is the `Coupon.couponId` (UUID). The
   * backend accepts an EMPTY body (no notes field per the controller
   * doc comment — mirrors the ProductBundle approve flow but with NO
   * optional notes field for coupons).
   *
   * Spec ref: SPEC-242 §3.5 (PUT /admin/market/coupons/:couponId/
   *           approve), §3.4.1 (INV-MKT-20 admin approval gate);
   *           SPEC-NEGOCIO §10.4 (INV-MKT-20) + §9.6 (INV-SEC-06).
   */
  async approve(
    couponId: string,
    signal?: AbortSignal,
  ): Promise<Coupon> {
    return apiFetch<Coupon>(
      `/admin/market/coupons/${encodeURIComponent(couponId)}/approve`,
      {
        method: 'PUT',
        body: JSON.stringify({}),
        signal,
      },
    );
  },
  /**
   * PUT /api/admin/market/coupons/:couponId/reject — reject a
   * pending coupon (INV-MKT-20). The backend atomically transitions
   * `status: 'pending_approval' → 'rejected'` via CAS. The
   * `reason` is MANDATORY (Zod `rejectReasonSchema` — 10-500 chars)
   * — the seller needs to know why their coupon was rejected so
   * they can fix + resubmit. The reason is recorded in the
   * AuditLog metadata (the `Coupon` model has no `adminNotes`
   * field per SPEC-242 §3.3.1; if a future spec requires persisting
   * the reason on the document, the model will need to be extended).
   *
   * 2FA required (INV-SEC-06 §9.6 — same rationale as `approve`).
   *
   * Path param: `couponId` is the `Coupon.couponId` (UUID).
   *
   * Spec ref: SPEC-242 §3.5 (PUT /admin/market/coupons/:couponId/
   *           reject), §3.4.1 (INV-MKT-20 admin approval gate);
   *           SPEC-NEGOCIO §10.4 (INV-MKT-20) + §9.6 (INV-SEC-06).
   */
  async reject(
    couponId: string,
    reason: string,
    signal?: AbortSignal,
  ): Promise<Coupon> {
    return apiFetch<Coupon>(
      `/admin/market/coupons/${encodeURIComponent(couponId)}/reject`,
      {
        method: 'PUT',
        body: JSON.stringify({ reason }),
        signal,
      },
    );
  },
};
